import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Popconfirm, Select, Space, Switch, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { http } from '../../api/client.js';
import { sipConfigStore } from '../../api/sip-config.js';
import { type SipCallingAssignment, sipPhoneApi } from '../../api/sip-phone-api.js';

interface UserOption {
  systemuserid: string;
  fullname: string | null;
  domainname: string | null;
  internalemailaddress: string | null;
  isdisabled: boolean | null;
}

interface AssignmentForm {
  userId: string;
  extension: string;
}

export function SipAssignmentCard() {
  const cfg = useSyncExternalStore(
    sipConfigStore.subscribe,
    sipConfigStore.get,
    sipConfigStore.get,
  );
  const [assignmentForm] = Form.useForm<AssignmentForm>();
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [assignments, setAssignments] = useState<SipCallingAssignment[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const userSearchSeq = useRef(0);

  const loadAssignments = useCallback(() => {
    setLoadingAssignments(true);
    sipPhoneApi
      .listCallingAssignments()
      .then(setAssignments)
      .catch(() => message.error('加载号码分配失败'))
      .finally(() => setLoadingAssignments(false));
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const searchUsers = useCallback(
    (q = '') => {
      userSearchSeq.current += 1;
      const seq = userSearchSeq.current;
      setLoadingUsers(true);
      http
        .get<{ rows: UserOption[] }>('/admin/users', {
          params: { q, page: 1, pageSize: q.trim() ? 50 : 200 },
        })
        .then((r) => {
          if (seq !== userSearchSeq.current) return;
          setUsers(r.data.rows);
        })
        .catch(() => {
          if (seq === userSearchSeq.current && !q.trim()) setUsers([]);
        })
        .finally(() => {
          if (seq === userSearchSeq.current) setLoadingUsers(false);
        });
    },
    [],
  );

  useEffect(() => {
    searchUsers();
  }, [searchUsers]);

  const usedExtensions = useMemo(
    () => new Set(assignments.map((item) => item.extension)),
    [assignments],
  );

  const extensionOptions = useMemo(() => {
    const startText = String(cfg.extensionStart ?? '');
    const endText = String(cfg.extensionEnd ?? '');
    const start = Number(startText);
    const end = Number(endText);
    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || end < start) return [];
    const width = Math.max(startText.length, endText.length);
    return Array.from({ length: Math.min(end - start + 1, 1000) }, (_, index) => {
      const value = String(start + index).padStart(width, '0');
      return { value, label: value, disabled: usedExtensions.has(value) };
    });
  }, [cfg.extensionEnd, cfg.extensionStart, usedExtensions]);

  const userOptions = useMemo(() => {
    const assignedUsers = new Set(assignments.map((item) => item.userId));
    return users.map((u) => {
      const name = u.fullname ?? u.domainname ?? u.systemuserid;
      const email = u.internalemailaddress ? ` (${u.internalemailaddress})` : '';
      return {
        value: u.systemuserid,
        label: `${name}${email}`,
        disabled: assignedUsers.has(u.systemuserid) || u.isdisabled === true,
      };
    });
  }, [assignments, users]);

  const handleAddAssignment = async () => {
    try {
      const values = await assignmentForm.validateFields();
      setSavingAssignment(true);
      await sipPhoneApi.saveCallingAssignment({ ...values, enabled: true });
      assignmentForm.resetFields();
      message.success('号码分配已添加');
      loadAssignments();
      window.dispatchEvent(new Event('sip-calling-assignment-changed'));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? '添加号码分配失败');
    } finally {
      setSavingAssignment(false);
    }
  };

  const toggleAssignment = async (row: SipCallingAssignment, enabled: boolean) => {
    try {
      await sipPhoneApi.updateCallingAssignment(row.userId, { enabled });
      setAssignments((prev) =>
        prev.map((item) => (item.userId === row.userId ? { ...item, enabled } : item)),
      );
      message.success(enabled ? '已启动该用户通话功能' : '已停用该用户通话功能');
      window.dispatchEvent(new Event('sip-calling-assignment-changed'));
    } catch {
      message.error('更新状态失败');
    }
  };

  const removeAssignment = async (row: SipCallingAssignment) => {
    try {
      await sipPhoneApi.deleteCallingAssignment(row.userId);
      setAssignments((prev) => prev.filter((item) => item.userId !== row.userId));
      message.success('已移除号码分配');
      window.dispatchEvent(new Event('sip-calling-assignment-changed'));
    } catch {
      message.error('移除号码分配失败');
    }
  };

  const columns: ColumnsType<SipCallingAssignment> = [
    {
      title: '用户',
      render: (_, row) => row.user.fullname ?? row.user.domainname ?? row.userId,
    },
    { title: '邮箱', dataIndex: ['user', 'internalemailaddress'] },
    { title: '电话号码', dataIndex: 'extension', width: 130 },
    {
      title: '状态',
      width: 120,
      render: (_, row) => (
        <Space>
          <Switch checked={row.enabled} size="small" onChange={(v) => toggleAssignment(row, v)} />
          <Tag color={row.enabled ? 'green' : 'default'}>{row.enabled ? '启动' : '停用'}</Tag>
        </Space>
      ),
    },
    {
      title: '操作',
      width: 90,
      render: (_, row) => (
        <Popconfirm
          title="移除号码分配？"
          okText="移除"
          cancelText="取消"
          onConfirm={() => removeAssignment(row)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card title="号码分配">
      <Form<AssignmentForm>
        form={assignmentForm}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}
      >
        <Form.Item
          name="userId"
          rules={[{ required: true, message: '请选择用户' }]}
          style={{ minWidth: 280 }}
        >
          <Select
            showSearch
            placeholder="选择用户管理中已存在的用户"
            optionFilterProp="label"
            filterOption={false}
            loading={loadingUsers}
            onSearch={searchUsers}
            options={userOptions}
          />
        </Form.Item>
        <Form.Item
          name="extension"
          rules={[{ required: true, message: '请选择电话号码' }]}
          style={{ minWidth: 180 }}
        >
          <Select
            showSearch
            placeholder="选择可用电话号码"
            optionFilterProp="label"
            options={extensionOptions}
          />
        </Form.Item>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={savingAssignment}
          onClick={handleAddAssignment}
        >
          添加用户
        </Button>
      </Form>

      <Table<SipCallingAssignment>
        rowKey="userId"
        size="small"
        loading={loadingAssignments}
        columns={columns}
        dataSource={assignments}
        pagination={{ pageSize: 20 }}
      />
    </Card>
  );
}
